// lib/origen-suscripcion.js
// De dónde salió la persona que se suscribe: el post del blog, la app o la pantalla con el
// botón que la mandó a /subscribe. Pedido de Fede (3-oct-2026): saber qué les interesa más.
//
// Cada botón que lleva a /subscribe le cuelga ?from=<origen> con conOrigen(), la pantalla de
// suscripción lo guarda al abrir (capturarOrigen) y el pago lo manda en `ad.origen`, que
// termina en subscriptions.origen (admin-back: App\Services\OrigenSuscripcion). Los códigos:
//
//   blog:<id>        el candado de un post (BlogPostView). El id del post y no el slug, que
//                    cambia con el idioma
//   app:<clave>      el cartel de la propia app (AppAccessGate)
//   grilla:<clave>   el modal de la grilla de /apps
//   menu:<clave>     el modal del menú de apps del header
//   landing:<clave>  la landing de una app (/aprender-partitura)
//   3d-app           las funciones premium del Libro 3D (PremiumTooltip). Ya existía, para
//                    abrir el panel de la app al llegar, y quedó igual
//   portal           "Mi suscripción" del portal, para quien no tiene una
//
// <clave> es la de APP_UI en minúscula: BCM_NODES -> bcm_nodes.
//
// Se guarda como el resto de la atribución de la casa (adparams.js, reflink.js): cookie propia
// más espejo en localStorage, porque ITP capa a 7 días las cookies escritas por JS. Vale 30 días
// y gana el ÚLTIMO botón. Así, el que tocó Suscribirme en un post, se fue y volvió a los dos días
// directo a /subscribe sigue contando para el post, y la edad en minutos que viaja con el código
// separa al que pagó en el momento del que lo pensó.

import { getCookie } from "@/utils/cookies";

const KEY = "bcm_sub_origen";
const MAX_AGE_DAYS = 30;

/** El formato que acepta el back (OrigenSuscripcion::ORIGEN_RE) y que entra en la columna. */
const ORIGEN_RE = /^[a-z0-9][a-z0-9:_-]{0,79}$/;

/** /subscribe con o sin idioma adelante. El /subscribe/success no: ahí ya pagó. */
const LINK_SUSCRIPCION_RE = /^(?:\/[a-z]{2})?\/subscribe(?:[?#]|$)/;

/**
 * El código tal como se guarda: "app:BCM_NODES" -> "app:bcm_nodes". Se normaliza en vez de
 * rechazar porque la clave de APP_UI viene en mayúscula. Devuelve null si no queda nada usable.
 */
export function normalizarOrigen(valor) {
  if (typeof valor !== "string") return null;
  const s = valor
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9:_-]+/g, "-")
    .replace(/^[-_:]+/, "")
    .slice(0, 80)
    .replace(/[-_:]+$/, "");
  return ORIGEN_RE.test(s) ? s : null;
}

/** ¿El link va a la pantalla de suscripción? Solo esos se marcan. */
export function esLinkDeSuscripcion(href) {
  return typeof href === "string" && LINK_SUSCRIPCION_RE.test(href);
}

/**
 * El mismo link con ?from=<origen>. Cualquier otro link vuelve igual, así que se le puede pasar
 * el buyPath de cualquier app: el checkout de libros no lee ?from= y no hay por qué tocarle la
 * URL. Si el link ya traía un from (el menú del header copia la query de la página en la que
 * está) se pisa: el botón que se toca ahora es el que vale.
 *
 * Corre mientras se dibujan el blog, las apps y el portal, así que no puede tirar nunca: ante
 * cualquier cosa rara devuelve el link tal como vino, que es como estaba antes de esto.
 */
export function conOrigen(href, origen) {
  try {
    const codigo = normalizarOrigen(origen);
    if (!codigo || !esLinkDeSuscripcion(href)) return href;

    const [sinHash, ...resto] = href.split("#");
    const hash = resto.length ? `#${resto.join("#")}` : "";
    const i = sinHash.indexOf("?");
    const path = i === -1 ? sinHash : sinHash.slice(0, i);
    const params = new URLSearchParams(i === -1 ? "" : sinHash.slice(i + 1));
    params.set("from", codigo);
    // Los dos puntos son válidos en una query: se dejan legibles (from=app:mosca).
    return `${path}?${params.toString().replace(/%3A/gi, ":")}${hash}`;
  } catch {
    return href;
  }
}

function persistir(obj) {
  const raw = JSON.stringify(obj);
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${KEY}=${encodeURIComponent(raw)}; max-age=${maxAge}; path=/; SameSite=Lax`;
  try {
    window.localStorage.setItem(KEY, raw);
  } catch {
    /* localStorage puede estar bloqueado (modo privado) */
  }
}

/** Lo guardado y vigente, o null. Se valida al LEER porque la cookie es del cliente. */
function leer() {
  if (typeof window === "undefined") return null;

  const fuentes = [];
  const cookie = getCookie(KEY);
  if (cookie) fuentes.push(cookie);
  try {
    const ls = window.localStorage?.getItem(KEY);
    if (ls) fuentes.push(ls);
  } catch {
    /* ignore */
  }

  for (const raw of fuentes) {
    try {
      const obj = JSON.parse(raw);
      if (!obj || typeof obj !== "object") continue;
      if (!ORIGEN_RE.test(String(obj.o ?? ""))) continue;
      if (!Number.isFinite(obj.t) || obj.t <= 0) continue;
      if (Date.now() - obj.t > MAX_AGE_DAYS * 864e5) continue;
      return obj;
    } catch {
      /* valor corrupto: se ignora y se prueba la otra fuente */
    }
  }
  return null;
}

/**
 * Lee ?from= de la URL y lo guarda. Lo llama la pantalla de suscripción al abrir; navegar sin
 * ?from= no toca lo guardado, y un from vacío o basura tampoco lo borra.
 */
export function capturarOrigen(search) {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(search ?? window.location.search);
    const codigo = normalizarOrigen(params.get("from"));
    if (!codigo) return;
    persistir({ o: codigo, t: Date.now() });
  } catch {
    /* nunca romper la página por analytics */
  }
}

/**
 * El botón vigente para mandar en el pago, o null si la persona no pasó por ninguno.
 * `edadMin` son los minutos desde que lo tocó (misma idea que adparams.js y reflink.js).
 */
export function getOrigen() {
  try {
    const obj = leer();
    if (!obj) return null;
    return { code: obj.o, edadMin: Math.max(0, Math.round((Date.now() - obj.t) / 60000)) };
  } catch {
    return null;
  }
}
